// Vendor Libs
import React, {Component} from 'react';
import PropTypes from 'prop-types';
import {connect} from 'react-redux';
import {get} from 'lodash';

// Components
import Button from '../core/button';
import Error from '../core/error';
import FormConfirm from '../core/form-confirm';
import MediaItemOfferingsForm from './mediaitem-offerings-form';
import Pagination from '../core/pagination';
import Permissions from '../core/permissions';
import RecordDetails from '../core/record-details';
import RecordLocker from './record-locker';

// Containers
import withForm from '../../containers/with-form';

// Core Libs
import {formatDate} from '../../lib/core/date';
import {isEqualJson} from '../../lib/core/object';

// Redux Actions
import {deleteOffering, deleteOfferingReset} from '../../actions/delete-offering-actions';

// Contexts
import {TabContext} from '../../contexts/tab-context';

// Styles
import './edit-mediaitem-offering.scss';

const formName = 'editMediaItemOffering';

class EditMediaItemOffering extends Component {
    state = {deleteConfirm: false, deleteCompleted: false};

    componentWillMount() {
        this.setFormData();
        this.setFormCustomPath();
    }

    componentWillReceiveProps(nextProps) {
        const {editIndex, searchValues} = this.props;
        const {editIndex: nextEditIndex, searchValues: nextSearchValues} = nextProps;

        if (
            !isEqualJson(
                get(nextSearchValues, ['offerings', nextEditIndex], {}),
                get(searchValues, ['offerings', editIndex], {})
            )
        ) {
            this.setFormData(nextProps);
            this.setFormCustomPath(nextProps);
        }
    }

    getAddButtonProps() {
        return {
            className: 'edit-mediaitem-offering--add-button',
            onClick: this.handleAddButtonClick,
            value: 'Add Offering',
        };
    }

    getCurrentOffering(props = this.props) {
        return get(this.getOfferings(props), props.editIndex, {});
    }

    getDeleteButtonProps() {
        const {editIndex} = this.props;

        return {
            className: 'edit-mediaitem-offering--delete-button',
            disabled: editIndex < 0 || this.getOfferingsCount() <= 1,
            onClick: this.handleDeleteShowFormClick,
            value: 'Delete Offering',
        };
    }

    getFormConfirmProps() {
        const {deleteCompleted} = this.state;
        const {deleteOfferingError, deleteOfferingResponse} = this.props;

        return {
            completed: deleteCompleted,
            confirmMessage: 'Are you sure you want to delete this offering?',
            confirmLabel: 'Delete Offering',
            error: deleteOfferingError,
            formSubmitReset: this.resetDeleteForm,
            onComplete: this.handleDeleteFormResetClick,
            onCancel: this.resetDeleteForm,
            onConfirm: this.handleDeleteButtonClick,
            response: deleteOfferingResponse,
            searchData: this.getCurrentOffering(),
            whitelistKeys: ['offeringId', 'resourceId', 'startDate', 'endDate'],
        };
    }

    getOfferings(props = this.props) {
        return props.searchValues.offerings || [];
    }

    getOfferingsCount() {
        return this.getOfferings().length;
    }

    getPaginationProps() {
        const {editIndex} = this.props;

        return {
            children: 'Offerings:',
            className: 'edit-mediaitem-offering--pagination',
            currentPage: editIndex + 1,
            onClick: this.handlePaginationClick,
            totalPages: this.getOfferingsCount(),
        };
    }

    setFormData(props = this.props) {
        const {setFieldValues} = this.props;
        const currentOffering = this.getCurrentOffering(props);

        setFieldValues({
            ...currentOffering,
            endDate: formatDate(currentOffering.endDate, 'YYYY-MM-DDTHH:mm:ssZ'),
            startDate: formatDate(currentOffering.startDate, 'YYYY-MM-DDTHH:mm:ssZ'),
            streetDate: formatDate(currentOffering.streetDate, 'YYYY-MM-DD'),
        });
    }

    setFormCustomPath(props = this.props) {
        const {setFormCustomPath} = this.props;

        setFormCustomPath(props.searchValues.guid);
    }

    handleAddButtonClick = (event) => {
        const {setEditCreateState} = this.props;

        event && event.preventDefault && event.preventDefault();

        setEditCreateState(false);
    };

    handleCancelButtonClick = () => {
        const {selectTab} = this.context;

        this.setFormData();
        selectTab('MEDIAITEMDETAIL');
    };

    handleDeleteButtonClick = () => {
        const {deleteOffering, searchValues} = this.props;
        const {offeringId, resourceId} = this.getCurrentOffering();

        deleteOffering({guid: searchValues.guid, offeringId, resourceId}).then(() => {
            this.setState({deleteConfirm: false, deleteCompleted: true});
        });
    };

    handleDeleteFormResetClick = () => {
        this.handleFormReset();
        this.resetDeleteForm();
    };

    handleDeleteShowFormClick = (event) => {
        event && event.preventDefault && event.preventDefault();

        this.setState({deleteConfirm: true, deleteCompleted: false});
    };

    handleFormReset = () => {
        this.setFormData();
    };

    handlePaginationClick = (pageNumber) => {
        const {setEditIndex} = this.props;

        if (pageNumber <= this.getOfferingsCount()) {
            setEditIndex(pageNumber - 1);
        }
    };

    resetDeleteForm = () => {
        const {deleteOfferingReset} = this.props;

        deleteOfferingReset();
        this.setState({deleteConfirm: false, deleteCompleted: false});
    };

    shouldRenderEditRecordField = (formData, name) => {
        let visible = false;

        if (name === 'threeDFormat') {
            visible = get(formData, 'resolution') === '3D';
        }

        return visible;
    };

    shouldRenderFormConfirm() {
        const {deleteCompleted, deleteConfirm} = this.state;

        return deleteConfirm || deleteCompleted;
    }

    renderContent() {
        return this.shouldRenderFormConfirm() ? <FormConfirm {...this.getFormConfirmProps()} /> : this.renderForm();
    }

    renderCreateDeleteButtons() {
        return (
            <div className="edit-mediaitem-offering--button-group">
                <Button {...this.getAddButtonProps()} />
                <Button {...this.getDeleteButtonProps()} />
            </div>
        );
    }

    renderForm() {
        const {editIndex, offeringWhitelistKeys = ['offeringId'], searchValues} = this.props;
        const currentOffering = this.getCurrentOffering();
        const formProps = {
            currentOffering,
            editing: true,
            formName,
            offerings: this.getOfferings(),
            onCancelClick: this.handleCancelButtonClick,
            serviceName: get(searchValues, 'service.serviceName'),
        };
        const {guid} = searchValues;

        return editIndex > -1 && guid ? (
            <Permissions
                accessRequirement="write"
                lockAccess={true}
                notAuthorizedMessage={true}
                recordId={guid}
                requiresServiceAccess={true}
                serviceKey="mediaItems"
            >
                {this.renderRecordLocker()}
                {this.renderCreateDeleteButtons()}
                <Pagination {...this.getPaginationProps()} />
                <RecordDetails
                    className="edit-mediaitem-offering--record"
                    searchData={[currentOffering]}
                    whitelistKeys={offeringWhitelistKeys}
                />
                <MediaItemOfferingsForm {...formProps} />
            </Permissions>
        ) : (
            <Error error="Choose a record." />
        );
    }

    renderRecordLocker() {
        const {searchValues} = this.props;
        const recordId = get(searchValues, 'guid');

        return recordId ? <RecordLocker recordId={recordId} serviceKey="mediaItems" /> : null;
    }

    render() {
        return <div className="edit-mediaitem-offering">{this.renderContent()}</div>;
    }
}

EditMediaItemOffering.propTypes = {
    deleteOffering: PropTypes.func.isRequired,
    deleteOfferingError: PropTypes.string,
    deleteOfferingFetched: PropTypes.bool,
    deleteOfferingFetching: PropTypes.bool,
    deleteOfferingReset: PropTypes.func.isRequired,
    deleteOfferingResponse: PropTypes.string,
    editIndex: PropTypes.number.isRequired,
    offeringWhitelistKeys: PropTypes.array,
    searchValues: PropTypes.object.isRequired,
    setEditCreateState: PropTypes.func.isRequired,
    setEditIndex: PropTypes.func.isRequired,
    setFieldValues: PropTypes.func.isRequired,
    setFormCustomPath: PropTypes.func.isRequired,
    user: PropTypes.string.isRequired,
};

EditMediaItemOffering.contextType = TabContext;

const mapStateToProps = (state) => ({
    deleteOfferingError: state.deleteOffering.error,
    deleteOfferingFetching: state.deleteOffering.fetching,
    deleteOfferingFetched: state.deleteOffering.fetched,
    deleteOfferingResponse: state.deleteOffering.response,
    user: state.auth.user,
});

const mapDispatchToProps = {deleteOffering, deleteOfferingReset};

export default withForm(connect(mapStateToProps, mapDispatchToProps)(EditMediaItemOffering), formName);
